// World art templates (art-assets §5). In the running game these objects are drawn procedurally
// by the renderer in the active style (classic by default, or sketch), so these bitmaps are the
// size-correct templates `npm run export:placeholders` writes for the artist to trace over. They
// follow the placement rule: the object's body fills a circle of diameter 2/k of the image side.
// Tinted slots (pellets, ejected) are drawn white with ink lines, as the multiply tint expects.

import { mixRgb, SPECIAL_COLORS } from '@ballio/shared';
import type { Canvas2D } from '../../render/canvas.js';
import { css } from '../../render/theme.js';
import {
  art,
  fillWith,
  INK,
  inkCirclePath,
  PAPER_DARK,
  PAPER_LIGHT,
  spikyPath,
  strokeInk,
  TAU,
  WHITE,
} from './draw.js';

/** Virus spikes for a body radius (architecture §9.3: clamp(0.18·r, 12, 60), r in world units). */
function spikesFor(r: number): number {
  return Math.min(60, Math.max(12, Math.round(0.18 * r)));
}

function drawVirusLike(
  size: number,
  k: number,
  color: number,
  worldR: number,
  fed: boolean,
  seed: number,
): Canvas2D | null {
  return art(size, size, (ctx, w, h) => {
    const r = Math.min(w, h) / k; // body radius in px: diameter 2/k of the side
    const cx = w / 2;
    const cy = h / 2;
    const lw = Math.max(2, r * 0.05);
    ctx.beginPath();
    spikyPath(ctx, cx, cy, r, spikesFor(worldR), 0.08, seed, 0.02);
    fillWith(ctx, fed ? mixRgb(color, WHITE, 0.25) : color);
    strokeInk(ctx, lw);
    ctx.setLineDash([r * 0.12, r * 0.08]);
    ctx.beginPath();
    inkCirclePath(ctx, cx, cy, r * 0.72, seed + 1, 0.01);
    strokeInk(ctx, lw * 0.6, INK, 0.6);
    ctx.setLineDash([]);
  });
}

/** `world/virus.png`: a virus-green spiky circle, ink outline, dashed inner ring (512 px, k 2.3). */
export function drawVirusTemplate(width = 512, height = width): Canvas2D | null {
  return drawVirusLike(Math.min(width, height), 2.3, SPECIAL_COLORS.virus, 100, false, 31);
}

/** `world/virus-fed.png`: the nearly-full virus (lighter, as it swells before shooting). */
export function drawVirusFedTemplate(width = 512, height = width): Canvas2D | null {
  return drawVirusLike(Math.min(width, height), 2.3, SPECIAL_COLORS.virus, 130, true, 32);
}

/** `world/spawner.png`: the virus style in the spawner colour. */
export function drawSpawnerTemplate(width = 512, height = width): Canvas2D | null {
  return drawVirusLike(Math.min(width, height), 2.3, SPECIAL_COLORS.spawner, 149, false, 33);
}

/** A tint template: a white disc with an ink outline, body diameter 2/k of the side. */
function drawTintDisc(size: number, k: number, seed: number, highlight: boolean): Canvas2D | null {
  return art(size, size, (ctx, w, h) => {
    const r = Math.min(w, h) / k;
    ctx.beginPath();
    inkCirclePath(ctx, w / 2, h / 2, r, seed, 0.02, 48);
    fillWith(ctx, WHITE);
    strokeInk(ctx, Math.max(1.5, r * 0.08));
    if (highlight) {
      ctx.beginPath();
      ctx.arc(w / 2 - r * 0.32, h / 2 - r * 0.32, r * 0.22, 0, TAU);
      fillWith(ctx, 0xe8e8e8);
    }
  });
}

/** `world/pellets/pellet-NN.png`: 64 px, k 2.5, white for the multiply tint. */
export function drawPelletTemplate(width = 64, height = width, variant = 1): Canvas2D | null {
  return drawTintDisc(Math.min(width, height), 2.5, 400 + variant, true);
}

/** `world/ejected.png`: 128 px, k 2.4, white for the multiply tint. */
export function drawEjectedTemplate(width = 128, height = width): Canvas2D | null {
  return drawTintDisc(Math.min(width, height), 2.4, 77, false);
}

/** `world/cell-overlay.png`: white with a faint grey grain inside the cell circle (k 2.0). */
export function drawCellOverlayTemplate(width = 512, height = width): Canvas2D | null {
  return art(width, height, (ctx, w, h) => {
    const r = Math.min(w, h) / 2;
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, r, 0, TAU);
    fillWith(ctx, WHITE);
    ctx.save();
    ctx.clip();
    for (let i = 0; i < 900; i++) {
      // Deterministic speckles (golden-angle spiral), slightly darker than white.
      const t = i / 900;
      const a = i * 2.399963;
      const rr = Math.sqrt(t) * r;
      ctx.fillStyle = css(mixRgb(WHITE, 0x808080, 0.12 + (0.1 * ((i * 37) % 7)) / 7));
      ctx.fillRect(w / 2 + rr * Math.cos(a), h / 2 + rr * Math.sin(a), 3, 3);
    }
    ctx.restore();
  });
}

/** `world/paper-light.png` / `paper-dark.png`: the flat paper colour (a seamless tile). */
export function drawPaperTemplate(width = 512, height = 512, dark = false): Canvas2D | null {
  return art(width, height, (ctx, w, h) => {
    ctx.fillStyle = css(dark ? PAPER_DARK : PAPER_LIGHT);
    ctx.fillRect(0, 0, w, h);
  });
}
